import React, { useState, useEffect, useCallback } from 'react';
import { Row, Col, Card, Table, Button, Badge, Alert, Modal, Form, Spinner } from 'react-bootstrap';
import axios from 'axios';

interface WithdrawalRequest {
  _id: string;
  user: {
    _id: string;
    name: string;
    email: string;
    rewardBalance: number;
  };
  amount: number;
  bankDetails: {
    accountNumber: string;
    ifscCode: string;
    bankName: string;
    accountHolderName: string;
    accountType: string;
  };
  status: 'pending' | 'approved' | 'rejected' | 'processed';
  createdAt: string;
  processedAt?: string;
  adminResponse?: string;
  transactionId?: string;
  reviewedBy?: {
    _id: string;
    name: string;
    email: string;
  };
  userBalance: number;
  ipAddress?: string;
}

const AdminWithdrawalManagement: React.FC = () => {
  const [requests, setRequests] = useState<WithdrawalRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedRequest, setSelectedRequest] = useState<WithdrawalRequest | null>(null);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [transactionId, setTransactionId] = useState('');
  const [adminResponse, setAdminResponse] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'approved' | 'rejected' | 'processed'>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
  const requestsPerPage = 10;

  const loadWithdrawalRequests = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: requestsPerPage.toString(),
      });
      
      if (statusFilter !== 'all') {
        params.append('status', statusFilter);
      }

      const response = await axios.get(`${API_BASE_URL}/withdrawals/admin/all?${params}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.data.success) {
        setRequests(response.data.requests || []);
        setTotalCount(response.data.pagination?.total || 0);
        setTotalPages(response.data.pagination?.pages || 0);
      } else {
        setError(response.data.message || 'Failed to load withdrawal requests');
      }
    } catch (error: any) {
      console.error('Failed to load withdrawal requests:', error);
      setError(error.response?.data?.message || 'Failed to load withdrawal requests');
    } finally {
      setLoading(false);
    }
  }, [currentPage, statusFilter, API_BASE_URL]);

  useEffect(() => {
    loadWithdrawalRequests();
  }, [loadWithdrawalRequests]);

  const handleApproveRequest = async () => {
    if (!selectedRequest) return;

    try {
      setProcessing(true);
      const response = await axios.put(
        `${API_BASE_URL}/withdrawals/admin/${selectedRequest._id}/approve`,
        {
          transactionId: transactionId.trim(),
          adminResponse: adminResponse.trim()
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (response.data.success) {
        setSuccess('Withdrawal request approved successfully');
        setShowApprovalModal(false);
        resetModalState();
        loadWithdrawalRequests();
      } else {
        setError(response.data.message || 'Failed to approve withdrawal request');
      }
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to approve withdrawal request');
    } finally {
      setProcessing(false);
    }
  };

  const handleRejectRequest = async () => {
    if (!selectedRequest) return;

    try {
      setProcessing(true);
      const response = await axios.put(
        `${API_BASE_URL}/withdrawals/admin/${selectedRequest._id}/reject`,
        {
          adminResponse: adminResponse.trim()
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (response.data.success) {
        setSuccess('Withdrawal request rejected successfully');
        setShowRejectionModal(false);
        resetModalState();
        loadWithdrawalRequests();
      } else {
        setError(response.data.message || 'Failed to reject withdrawal request');
      }
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to reject withdrawal request');
    } finally {
      setProcessing(false);
    }
  };

  const resetModalState = () => {
    setSelectedRequest(null);
    setTransactionId('');
    setAdminResponse('');
  };

  const openApprovalModal = (request: WithdrawalRequest) => {
    setSelectedRequest(request);
    setShowApprovalModal(true);
  };

  const openRejectionModal = (request: WithdrawalRequest) => {
    setSelectedRequest(request);
    setShowRejectionModal(true);
  };

  const getStatusBadge = (status: string) => {
    const variants: { [key: string]: string } = {
      pending: 'warning',
      approved: 'info',
      processed: 'success',
      rejected: 'danger'
    };
    return <Badge bg={variants[status] || 'secondary'}>{status.toUpperCase()}</Badge>;
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <div className="admin-card">
      <div className="admin-card-header">
        <Row className="align-items-center">
          <Col>
            <h5 className="mb-0">
              <i className="fas fa-money-bill-wave me-2"></i>
              Withdrawal Management
            </h5>
            <small className="text-muted">
              Manage user withdrawal requests ({totalCount} total)
            </small>
          </Col>
          <Col xs="auto">
            <Button
              variant="outline-primary"
              onClick={loadWithdrawalRequests}
              disabled={loading}
              className="me-2"
            >
              <i className={`fas ${loading ? 'fa-spinner fa-spin' : 'fa-sync-alt'} me-2`}></i>
              Refresh
            </Button>
            <Form.Select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value as any);
                setCurrentPage(1);
              }}
              style={{ width: 'auto', display: 'inline-block' }}
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="processed">Processed</option>
              <option value="rejected">Rejected</option>
            </Form.Select>
          </Col>
        </Row>
      </div>

      <div className="admin-card-body">
        {/* Alerts */}
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError('')}>
            <i className="fas fa-exclamation-triangle me-2"></i>
            {error}
          </Alert>
        )}

        {success && (
          <Alert variant="success" dismissible onClose={() => setSuccess('')}>
            <i className="fas fa-check-circle me-2"></i>
            {success}
          </Alert>
        )}

        {/* Withdrawal Requests Table */}
        <div className="table-responsive">
          <Table hover>
            <thead>
              <tr>
                <th>User</th>
                <th>Amount</th>
                <th>Bank Details</th>
                <th>Status</th>
                <th>Requested Date</th>
                <th>User Balance</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {requests.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-4">
                    <i className="fas fa-inbox fa-2x text-muted mb-3"></i>
                    <p className="text-muted mb-0">
                      {statusFilter === 'all' 
                        ? 'No withdrawal requests found.'
                        : `No ${statusFilter} withdrawal requests found.`
                      }
                    </p>
                  </td>
                </tr>
              ) : (
                requests.map(request => (
                  <tr key={request._id}>
                    <td>
                      <div>
                        <strong>{request.user.name}</strong>
                        <br />
                        <small className="text-muted">{request.user.email}</small>
                      </div>
                    </td>
                    <td>
                      <strong className="text-success">
                        {formatCurrency(request.amount)}
                      </strong>
                    </td>
                    <td>
                      <div>
                        <small>
                          <strong>{request.bankDetails.bankName}</strong><br />
                          <span className="text-muted">
                            {request.bankDetails.accountHolderName}<br />
                            ****{request.bankDetails.accountNumber.slice(-4)}
                          </span>
                        </small>
                      </div>
                    </td>
                    <td>{getStatusBadge(request.status)}</td>
                    <td>
                      <small>{new Date(request.createdAt).toLocaleDateString()}</small>
                      <br />
                      <small className="text-muted">
                        {new Date(request.createdAt).toLocaleTimeString()}
                      </small>
                    </td>
                    <td>
                      <span className="text-info">
                        {formatCurrency(request.userBalance)}
                      </span>
                    </td>
                    <td>
                      {request.status === 'pending' && (
                        <div className="btn-group-vertical btn-group-sm">
                          <Button
                            variant="success"
                            size="sm"
                            onClick={() => openApprovalModal(request)}
                            className="mb-1"
                          >
                            <i className="fas fa-check me-1"></i>
                            Approve
                          </Button>
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => openRejectionModal(request)}
                          >
                            <i className="fas fa-times me-1"></i>
                            Reject
                          </Button>
                        </div>
                      )}
                      {request.status !== 'pending' && request.adminResponse && (
                        <small className="text-muted">
                          <i className="fas fa-comment me-1"></i>
                          {request.adminResponse}
                        </small>
                      )}
                      {request.transactionId && (
                        <div>
                          <small className="text-success">
                            <i className="fas fa-receipt me-1"></i>
                            {request.transactionId}
                          </small>
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </div>

        {/* Pagination (always visible) */}
        <div className="d-flex justify-content-between align-items-center mt-4">
          <div>
            <small className="text-muted">
              Showing {totalCount === 0 ? 0 : ((currentPage - 1) * requestsPerPage) + 1} to {Math.min(currentPage * requestsPerPage, totalCount)} of {totalCount} requests
            </small>
          </div>
          <div className="btn-group">
            <Button
              variant="outline-primary"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(1)}
            >
              First
            </Button>
            <Button
              variant="outline-primary"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(currentPage - 1)}
            >
              Previous
            </Button>
            <Button variant="primary" disabled>
              {currentPage}
            </Button>
            <Button
              variant="outline-primary"
              disabled={currentPage === totalPages || totalPages === 0}
              onClick={() => setCurrentPage(currentPage + 1)}
            >
              Next
            </Button>
            <Button
              variant="outline-primary"
              disabled={currentPage === totalPages || totalPages === 0}
              onClick={() => setCurrentPage(totalPages)}
            >
              Last
            </Button>
          </div>
        </div>
      </div>

      {/* Approval Modal */}
      <Modal show={showApprovalModal} onHide={() => {setShowApprovalModal(false); resetModalState();}}>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-check-circle text-success me-2"></i>
            Approve Withdrawal Request
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedRequest && (
            <div>
              <div className="mb-3">
                <strong>User:</strong> {selectedRequest.user.name} ({selectedRequest.user.email})
              </div>
              <div className="mb-3">
                <strong>Amount:</strong> {formatCurrency(selectedRequest.amount)}
              </div>
              <div className="mb-3">
                <strong>Bank Details:</strong><br />
                {selectedRequest.bankDetails.bankName}<br />
                {selectedRequest.bankDetails.accountHolderName}<br />
                Account: {selectedRequest.bankDetails.accountNumber}<br />
                IFSC: {selectedRequest.bankDetails.ifscCode}
              </div>
              
              <Form.Group className="mb-3">
                <Form.Label>Transaction ID *</Form.Label>
                <Form.Control
                  type="text"
                  value={transactionId}
                  onChange={(e) => setTransactionId(e.target.value)}
                  placeholder="Enter bank transaction ID"
                  required
                />
                <Form.Text className="text-muted">
                  Enter the transaction ID from your bank transfer
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Admin Response (Optional)</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={adminResponse}
                  onChange={(e) => setAdminResponse(e.target.value)}
                  placeholder="Add any additional notes for the user..."
                />
              </Form.Group>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => {setShowApprovalModal(false); resetModalState();}}>
            Cancel
          </Button>
          <Button 
            variant="success" 
            onClick={handleApproveRequest}
            disabled={processing || !transactionId.trim()}
          >
            {processing ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Processing...
              </>
            ) : (
              <>
                <i className="fas fa-check me-2"></i>
                Approve & Process
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Rejection Modal */}
      <Modal show={showRejectionModal} onHide={() => {setShowRejectionModal(false); resetModalState();}}>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-times-circle text-danger me-2"></i>
            Reject Withdrawal Request
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedRequest && (
            <div>
              <div className="mb-3">
                <strong>User:</strong> {selectedRequest.user.name} ({selectedRequest.user.email})
              </div>
              <div className="mb-3">
                <strong>Amount:</strong> {formatCurrency(selectedRequest.amount)}
              </div>
              
              <Form.Group className="mb-3">
                <Form.Label>Rejection Reason *</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={adminResponse}
                  onChange={(e) => setAdminResponse(e.target.value)}
                  placeholder="Please provide a reason for rejection..."
                  required
                />
                <Form.Text className="text-muted">
                  This message will be shown to the user
                </Form.Text>
              </Form.Group>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => {setShowRejectionModal(false); resetModalState();}}>
            Cancel
          </Button>
          <Button 
            variant="danger" 
            onClick={handleRejectRequest}
            disabled={processing || !adminResponse.trim()}
          >
            {processing ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Processing...
              </>
            ) : (
              <>
                <i className="fas fa-times me-2"></i>
                Reject Request
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AdminWithdrawalManagement;